import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, setCustomerToken, getCustomerToken } from "@/lib/api";

const AUTH_HOST = "https://auth.emergentagent.com/";

const AuthContext = createContext(null);

// Emergent returns the one-time id in the hash fragment; match the raw URL.
export const extractSessionId = (url) => url?.match(/[?#&]session_id=([^&#]+)/)?.[1] ?? null;

// The Owner Portal has its own Google sign-in callback on the /owner route — never let the
// customer auth flow consume that one-time session_id; owner page handles it independently.
const isOwnerCallback = (url) => !!url && url.includes("/owner");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const exchanged = useRef(new Set());

  const clearSession = useCallback(async () => {
    setCustomerToken(null);
    setUser(null);
  }, []);

  const exchange = useCallback(async (sessionId) => {
    if (exchanged.current.has(sessionId)) return false;
    exchanged.current.add(sessionId);
    const { session_token, user: u } = await api.exchangeSession(sessionId);
    setCustomerToken(session_token);
    setUser(u);
    return true;
  }, []);

  const refresh = useCallback(async () => {
    const token = getCustomerToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      setUser(await api.getProfile());
    } catch {
      await clearSession();
    }
  }, [clearSession]);

  useEffect(() => {
    (async () => {
      try {
        const href = window.location.href;
        const sid = isOwnerCallback(href) ? null : extractSessionId(href);
        if (sid) {
          try {
            await exchange(sid);
            const url = new URL(href);
            url.searchParams.delete("session_id");
            url.hash = url.hash.replace(/([?#&])session_id=[^&#]+&?/, "$1").replace(/[?#&]$/, "");
            window.history.replaceState(window.history.state, "", url.toString());
            setLoading(false);
            return;
          } catch {
            /* fall through to stored session */
          }
        }
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [exchange, refresh]);

  const signIn = useCallback(() => {
    const redirectUrl = `${window.location.origin}/profile`;
    window.location.href = `${AUTH_HOST}?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* local cleanup still proceeds */
    }
    await clearSession();
  }, [clearSession]);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refresh, setUser }),
    [user, loading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
